// ============================================================================
// formCollect.ts — СБОР ЗНАЧЕНИЙ ФОРМЫ ПРИ СОХРАНЕНИИ (SRP; вынесено из
// AutoObjectForm.handleSave, чтобы снизить цикломатику).
//
// Чистые функции: зависят только от данных, НЕ от React-состояния компонента.
// Единый источник правил «что уходит при сохранении» (SSoT):
//   - saveData        — значения записи (кастомная форма: из customValues;
//                       автоформа: из полей + системные колонки);
//   - formAttributes  — виртуальные реквизиты формы (НЕТ физической колонки)
//                       → модулю в РеквизитыФормы (коллизия «Адрес» снята:
//                       fieldCodes ТОЛЬКО по column_name f.code);
//   - extraFields     — order/task (сервер пишет только существующие колонки).
// ============================================================================

export const SYSTEM_SKIP_CODES = ['id', 'created_at', 'updated_at', 'created_by', 'updated_by', 'status_changed_at'];
export const SYSTEM_EXTRA_CODES = ['owner_id', 'parent_id', 'is_group', 'is_marked', 'is_posted', 'posted_at'];

/** Собрать saveData: значения записи при сохранении.
 *  customForm — кастомная форма (из customValues); иначе автоформа (из data). */
export function collectSaveData(opts: {
  fields: Array<{ code?: string; name?: string }>;
  data: Record<string, unknown>;
  customValues: Record<string, unknown>;
  customForm: boolean;
}): Record<string, unknown> {
  const { fields, data, customValues, customForm } = opts;
  const saveData: Record<string, unknown> = {};
  if (customForm) {
    for (const [key, val] of Object.entries(customValues)) {
      if (val === undefined || val === null) continue; // null = «не меняли» (NOT NULL колонки)
      if (key.endsWith('_presentation')) continue; // серверные представления — не записываются
      saveData[key] = val;
    }
    return saveData;
  }
  // Автоформа: из полей объекта
  for (const f of fields) {
    const code = f.code || f.name || '';
    if (!code) continue;
    if (SYSTEM_SKIP_CODES.includes(code)) continue;
    const v = data[code];
    if (v !== undefined && v !== null) saveData[code] = v;
  }
  // Системные колонки (владелец/иерархия/пометка/проведение) — сохраняются
  for (const code of SYSTEM_EXTRA_CODES) {
    if (data[code] !== undefined) saveData[code] = data[code];
  }
  return saveData;
}

/** Собрать formAttributes — виртуальные реквизиты формы (нет физической колонки).
 *  ВАЖНО (коллизия «Адрес»/address_id): fieldCodes — ТОЛЬКО по f.code (column_name),
 *  НЕ по f.name (синоним) — иначе виртуальный «Адрес» (синоним address_id) считался
 *  физическим и не уходил в formAttributes → модуль не сводил адрес. */
export function collectFormAttributes(
  fields: Array<{ code?: string; name?: string }>,
  customValues: Record<string, unknown>,
): Record<string, string> {
  const fieldCodes = new Set(fields.map((f) => f.code || ''));
  const formAttributes: Record<string, string> = {};
  for (const [k, v] of Object.entries(customValues)) {
    if (v === undefined || v === null) continue;
    if (SYSTEM_SKIP_CODES.includes(k)) continue;
    if (k.endsWith('_presentation')) continue;
    if (fieldCodes.has(k)) continue; // физическая колонка — в saveData
    if (fieldCodes.has(k.split('.').pop() || '')) continue; // «Объект.поле» → последний сегмент
    formAttributes[k] = String(v);
  }
  return formAttributes;
}

/** Собрать extraFields (order/task) — значения записи без системных колонок. */
export function collectExtraFields(saveData: Record<string, unknown>): Record<string, string> {
  const extra: Record<string, string> = {};
  for (const [k, v] of Object.entries(saveData)) {
    if (SYSTEM_SKIP_CODES.includes(k)) continue;
    if (v === undefined || v === null) continue;
    extra[k] = String(v);
  }
  return extra;
}

/** Собрать values для SaveForm (кастомная форма табличного объекта) —
 *  из customValues, без серверных представлений и системных колонок. */
export function collectFormValues(customValues: Record<string, unknown>): Record<string, string> {
  const values: Record<string, string> = {};
  for (const [k, v] of Object.entries(customValues)) {
    if (v === undefined || v === null) continue;
    if (k.endsWith('_presentation')) continue;
    if (SYSTEM_SKIP_CODES.includes(k)) continue;
    values[k] = String(v);
  }
  return values;
}
